/**
 * FeaturedBlock Component
 * 
 * Displays a curated selection of specific menu items.
 * Items are referenced by ID and fetched from the database.
 * Useful for highlighting specials, bestsellers, or chef's picks.
 */

import React, { memo, useMemo, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { FeaturedBlockProps } from '@/types/menuBuilder';
import { useBranch } from '@/contexts/BranchContext';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { formatCurrency } from '@/utils/currencyUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight, Plus, Minus, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Branch } from '@/types/index';
import { useCart } from '@/contexts/CartContext';
import { PublicMenuData } from '@/hooks/usePublicMenuFullData';

interface MenuItem {
  id: string;
  name: string;
  description?: string;
  base_price: number;
  image_url?: string;
  is_available: boolean;
}

interface FeaturedBlockComponentProps {
  props: FeaturedBlockProps;
  isPreview?: boolean;
  branch?: Branch;
  enableRatings?: boolean;
  enableOrdering?: boolean;
  preloadedData?: PublicMenuData;
}

export const FeaturedBlock = memo(function FeaturedBlock({
  props,
  isPreview = false,
  branch,
  enableRatings = false,
  enableOrdering = false,
  preloadedData,
}: FeaturedBlockComponentProps) {
  const { t } = useTranslation();
  const { selectedBranchId } = useBranch(true);
  const { branches } = useFilteredBranches();
  
  const currentBranch = useMemo(() => {
    if (branch) return branch;
    return branches.find(b => b.id === selectedBranchId) || null;
  }, [branch, branches, selectedBranchId]);
  
  const currency = currentBranch?.currency || 'USD';
  const { addItem } = useCart();

  const { 
    title, 
    itemIds, 
    layout, 
    autoplay, 
    interval = 5, 
    showTitle,
    titleAlignment = 'left',
    columns = 2,
  } = props;

  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch specific items by their IDs
  const { data: fetchedItems = [], isLoading: isFetching } = useQuery({
    queryKey: ['featured-items', itemIds],
    queryFn: async () => {
      if (!itemIds || itemIds.length === 0) return [];

      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .in('id', itemIds);

      if (error) throw error;

      // Sort items by the order they appear in itemIds
      const sortedItems = itemIds
        .map((id) => data?.find((item) => item.id === id))
        .filter(Boolean) as MenuItem[];

      return sortedItems;
    },
    enabled: !preloadedData && !!itemIds && itemIds.length > 0,
  });

  const items = useMemo(() => {
    if (preloadedData?.menuItems && itemIds) {
      return itemIds
        .map((id) => preloadedData.menuItems.find((item: any) => item.id === id))
        .filter(Boolean) as MenuItem[];
    }
    return fetchedItems;
  }, [preloadedData, itemIds, fetchedItems]);

  const isLoading = !preloadedData && isFetching;

  // Autoplay carousel
  useEffect(() => {
    if (layout !== 'carousel' || !autoplay || items.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, interval * 1000);

    return () => clearInterval(timer);
  }, [layout, autoplay, interval, items.length]);

  // No items selected - show placeholder
  if (!itemIds || itemIds.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground bg-muted/30 rounded-lg">
        {isPreview ? null : t('menuBuilder.selectItems', 'Select items in properties')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {showTitle && title && <Skeleton className="h-8 w-48" />}
        <div className={cn('grid gap-4', `grid-cols-${columns}`)}>
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <div className="p-4 space-y-4">
      {/* Section Title */}
      {showTitle && title && (
        <h2 className={cn('text-xl font-semibold', alignmentClasses[titleAlignment])}>
          {title}
        </h2>
      )}

      {/* Items */}
      {layout === 'carousel' ? (
        // Carousel layout
        <div className="relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-300"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {items.map((item) => (
                <div key={item.id} className="w-full flex-shrink-0 px-1">
                  <FeaturedItemCard 
                    item={item} 
                    currency={currency}
                    enableRatings={enableRatings}
                    enableOrdering={enableOrdering}
                    onAddToCart={() => addItem(item)}
                  />
                </div>
              ))}
            </div>
          </div>
          
          {items.length > 1 && (
            <>
              <Button
                variant="outline"
                size="icon"
                className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full bg-background/80"
                onClick={() => setCurrentIndex((prev) => (prev - 1 + items.length) % items.length)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full bg-background/80"
                onClick={() => setCurrentIndex((prev) => (prev + 1) % items.length)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              {/* Dots indicator */}
              <div className="flex justify-center gap-1 mt-3">
                {items.map((_, idx) => (
                  <button
                    key={idx}
                    className={cn(
                      'w-2 h-2 rounded-full transition-colors',
                      idx === currentIndex ? 'bg-primary' : 'bg-muted'
                    )}
                    onClick={() => setCurrentIndex(idx)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        // Grid layout
        <div className={cn(
          'grid gap-4',
          columns === 1 && 'grid-cols-1',
          columns === 2 && 'grid-cols-2',
          columns === 3 && 'grid-cols-3',
        )}>
          {items.map((item) => (
            <FeaturedItemCard 
              key={item.id} 
              item={item} 
              currency={currency}
              enableRatings={enableRatings}
              enableOrdering={enableOrdering}
              onAddToCart={() => addItem(item)}
            />
          ))}
        </div>
      )}
    </div>
  );
});

// Sub-components

interface FeaturedItemCardProps {
  item: MenuItem;
  currency: string;
  className?: string;
  enableRatings?: boolean;
  enableOrdering?: boolean;
  onAddToCart?: () => void;
}

function FeaturedItemCard({
  item,
  currency,
  className,
  enableRatings = false,
  enableOrdering = false,
  onAddToCart,
}: FeaturedItemCardProps) {
  const { items: cartItems, updateQuantity, removeItem, addItem } = useCart();
  const cartItem = cartItems.find(i => i.menuItem.id === item.id);

  return (
    <div
      className={cn(
        'rounded-lg border bg-card overflow-hidden',
        !item.is_available && 'opacity-60',
        className
      )}
    >
      {/* Image */}
      <div className="aspect-square bg-muted relative">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-4xl">
            🍽️
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-3">
        <h3 className="font-medium line-clamp-1">{item.name}</h3>
        {item.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {item.description}
          </p>
        )}
        
        <div className="flex items-center justify-between mt-2">
          <p className="font-semibold">{formatCurrency(item.base_price, currency)}</p>
          
          {enableOrdering && item.is_available && (
            cartItem ? (
              <div className="flex items-center gap-1 bg-secondary/50 rounded-full px-1 h-8" onClick={(e) => e.stopPropagation()}>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-6 w-6 rounded-full hover:bg-background/80" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (cartItem.quantity <= 1) {
                      removeItem(item.id);
                    } else {
                      updateQuantity(item.id, cartItem.quantity - 1);
                    }
                  }}
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="text-sm font-medium w-4 text-center">{cartItem.quantity}</span>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-6 w-6 rounded-full hover:bg-background/80" 
                  onClick={(e) => {
                    e.stopPropagation();
                    updateQuantity(item.id, cartItem.quantity + 1);
                  }}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="h-8 w-8 p-0 rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  addItem(item);
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )
          )}
        </div>

        {enableRatings && (
          <div className="mt-1 flex items-center gap-1">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            <span className="text-xs text-muted-foreground">4.8 (12)</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default FeaturedBlock;
